import { MessageSquare, ArrowUpRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import Reveal from "../motion/Reveal";

const FAQS = [
  {
    q: "Who can become a Zenstrust member?",
    a: "Any Non-Resident Indian (NRI), Person of Indian Origin (PIO) or Overseas Citizen of India (OCI) holding a valid passport and a tax residency certificate from their country of stay. We currently onboard members from 32 countries.",
  },
  {
    q: "Do I need an NRE / NRO bank account before signing up?",
    a: "No. During onboarding we help you open NRE/NRO accounts with our partner banks if you don't already have them — usually within 5 working days, fully online.",
  },
  {
    q: "How is my data and money kept safe?",
    a: "Your investments are held in your own name with SEBI-regulated custodians, and funds move only through your own NRE/NRO accounts. Zenstrust never takes custody of your assets. Data is encrypted at rest (AES-256) and in transit (TLS 1.3).",
  },
  {
    q: "How does the AI Investment service actually work?",
    a: "We combine your goals, residency, FX exposure and India market signals into a personalised model portfolio that rebalances on a defined schedule. Every recommendation is human-reviewed before it reaches you, and you approve every action.",
  },
  {
    q: "What about taxes — Indian and in my country of residence?",
    a: "Our cross-border tax desk coordinates filings under the relevant DTAA (US, UK, UAE, Singapore, Canada, Australia and more). We generate Form 67, capital-gain statements and country-specific schedules your local CA can use directly.",
  },
  {
    q: "Can I repatriate my returns abroad?",
    a: "Yes — under the RBI's USD 1 million per financial year route. We prepare CA certificates (Form 15CA/CB), coordinate with your bank and ensure FEMA compliance end to end.",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 lg:py-28 relative overflow-hidden"
      style={{ background: "rgba(255,255,255,0.20)" }}
    >
      {/* Subtle texture */}
      <div aria-hidden className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />
      {/* Soft gold orb top-right */}
      <div
        aria-hidden
        className="absolute -top-16 right-[-6%] w-80 h-80 rounded-full opacity-[0.10] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-gold)) 0%, transparent 68%)" }}
      />
      {/* Sage orb bottom-left */}
      <div
        aria-hidden
        className="absolute bottom-[-10%] left-[-4%] w-72 h-72 rounded-full opacity-[0.08] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-sage)) 0%, transparent 68%)" }}
      />
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">

          {/* Left: title + support card */}
          <Reveal className="lg:col-span-5 space-y-6 lg:sticky lg:top-32 self-start">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <span className="eyebrow">FAQ</span>
            </div>
            <h2 className="font-display text-[2rem] md:text-[2.5rem] leading-[1.1] text-[hsl(var(--zt-teal))]">
              Questions <span className="text-[hsl(var(--zt-gold))]">we get</span>, often.
            </h2>
            <p className="text-[15px] text-[hsl(var(--zt-charcoal))]/80 leading-[1.7] max-w-sm font-body">
              Everything about eligibility, safety, taxes and moving money — answered in plain terms.
            </p>

            <div className="rounded-2xl bg-white border border-[hsl(var(--zt-beige-deep))] p-5 elev-1 max-w-sm">
              <div className="flex items-start gap-3.5">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--zt-gold))]/10 grid place-items-center flex-shrink-0">
                  <MessageSquare size={17} className="text-[hsl(var(--zt-gold))]" />
                </div>
                <div>
                  <p className="font-heading text-[14px] font-semibold text-[hsl(var(--zt-teal))]">
                    Still have a question?
                  </p>
                  <p className="font-body text-[13px] text-[hsl(var(--zt-charcoal))]/75 leading-relaxed mt-1">
                    A Zenstrust advisor responds within one business day.
                  </p>
                  <button
                    type="button"
                    onClick={() => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" })}
                    className="mt-3 inline-flex items-center gap-1.5 font-heading text-[12.5px] font-semibold text-[hsl(var(--zt-gold))] hover:gap-2.5 transition-all duration-200"
                  >
                    Talk to an advisor <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right: accordion */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full space-y-3">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`item-${i}`}
                  className="border border-[hsl(var(--zt-beige-deep))] rounded-xl bg-white px-5 elev-1 transition-colors duration-200 data-[state=open]:border-[hsl(var(--zt-gold))]/35"
                >
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="font-heading text-left text-[15px] font-semibold text-[hsl(var(--zt-teal))] py-5 hover:no-underline tracking-tight transition-colors duration-200 hover:text-[hsl(var(--zt-gold))] [&[data-state=open]]:text-[hsl(var(--zt-gold))]"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[14px] text-[hsl(var(--zt-charcoal))]/80 leading-[1.7] pb-5 pr-6 font-body">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

        </div>
      </div>
    </section>
  );
}
